import type jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  PDFQuoteData,
  buildPalette,
  loadLogo,
  getJsPdfImageFormat,
  resolveDocumentTitle,
  renderDocumentSubtitle,
  fitHeaderTitle,
  formatQuoteDate,
  calcValidityDate,
  buildItemRows,
  formatCurrency,
  renderDiagnosisSection,
  renderServiceOrderSections,
  renderPaymentSection,
  renderExtraFields,
  renderPixSection,
  renderPhotosSection,
  renderSignatureSection,
  renderFooter,
  clientDocumentLabel,
  pdfQuoteTotals,
  renderTotalsBreakdown,
  renderLogoWatermark,
} from './shared';

/**
 * "Corporativo" template — sober dark-gray header, no brand color in header.
 * Feel for engineering / consulting.
 */
export async function render(doc: jsPDF, data: PDFQuoteData): Promise<void> {
  const pageWidth = doc.internal.pageSize.getWidth();

  // Marca-d'agua da logo: PRIMEIRA instrucao de desenho da pagina.
  // jsPDF pinta na ordem das chamadas e nao tem z-index, entao desenhada
  // depois ela cobriria a tabela e o total em vez de ficar atras deles.
  await renderLogoWatermark(doc, data);
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  const contentWidth = pageWidth - margin * 2;

  const { primaryColor, grayLight, grayText, darkText, white } =
    buildPalette(data.brandColorRGB);
  const corporateDark: [number, number, number] = [31, 41, 55]; // #1f2937
  const corporateMid: [number, number, number] = [55, 65, 81]; // #374151
  const zebra: [number, number, number] = [243, 244, 246];

  const logoBase64 = await loadLogo(data.company?.logoUrl);

  // === DARK HEADER ===
  const headerHeight = 50;
  doc.setFillColor(...corporateDark);
  doc.rect(0, 0, pageWidth, headerHeight, 'F');

  doc.setTextColor(...white);
  let yPos = 12;
  doc.setFont('helvetica', 'bold');
  // A logo (28mm) ocupa o canto direito da faixa escura, quando existe.
  const tituloCabecalho = fitHeaderTitle(
    doc,
    resolveDocumentTitle(data),
    contentWidth - (logoBase64 ? 32 : 0),
    22,
  );
  doc.setFontSize(tituloCabecalho.size);
  doc.text(tituloCabecalho.text, margin, yPos);
  // Subtitulo: 0 de avanco quando nao ha (o PDF do historico nao muda).
  yPos += renderDocumentSubtitle(doc, data, {
    x: margin, y: yPos, maxWidth: contentWidth - (logoBase64 ? 32 : 0), color: white,
  });

  if (data.company?.name) {
    yPos += 8;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(data.company.name, margin, yPos);
  }

  const infoLines: string[] = [];
  if (data.company?.cnpj) infoLines.push(`CNPJ: ${data.company.cnpj}`);
  if (data.company?.address) infoLines.push(data.company.address);
  const contact: string[] = [];
  if (data.company?.phone) contact.push(`Tel ${data.company.phone}`);
  if (data.company?.email) contact.push(data.company.email);
  if (contact.length) infoLines.push(contact.join('  |  '));

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  infoLines.forEach((line) => {
    yPos += 4;
    const wrapped = doc.splitTextToSize(line, pageWidth / 2)[0];
    doc.text(wrapped, margin, yPos);
  });

  // Logo top-right
  if (logoBase64) {
    try {
      const logoSize = 28;
      const format = getJsPdfImageFormat(logoBase64);
      doc.addImage(logoBase64, format, pageWidth - margin - logoSize, 8, logoSize, logoSize);
    } catch {
      /* ignore */
    }
  }

  // Accent thin line (brand color) at bottom of header
  doc.setFillColor(...primaryColor);
  doc.rect(0, headerHeight, pageWidth, 1.5, 'F');

  // === CLIENT BLOCK ===
  yPos = headerHeight + 10;
  doc.setFillColor(...zebra);
  doc.rect(margin, yPos, contentWidth, 24, 'F');

  doc.setTextColor(...corporateDark);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('CLIENTE', margin + 4, yPos + 7);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(data.client.name || 'Não informado', margin + 4, yPos + 13);

  const rightCol: string[] = [];
  rightCol.push(`Emissão: ${formatQuoteDate(data.quoteDate)}`);
  if (data.validityDays && data.quoteDate) {
    rightCol.push(`Validade: ${calcValidityDate(data.quoteDate, data.validityDays)}`);
  }
  // 3ª linha cai em yPos+17, dentro do retângulo de 24 desenhado acima.
  if (data.reference) rightCol.push(`Ref.: ${data.reference}`);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  rightCol.forEach((line, i) => {
    doc.text(line, pageWidth - margin - 4, yPos + 7 + i * 5, { align: 'right' });
  });

  if (data.client.phone || data.client.address || data.client.document) {
    const contactLine: string[] = [];
    if (data.client.phone) contactLine.push(`Tel ${data.client.phone}`);
    if (data.client.document) contactLine.push(clientDocumentLabel(data.client.document));
    if (data.client.address) contactLine.push(data.client.address);
    doc.setFontSize(8);
    doc.setTextColor(...grayText);
    const line = doc.splitTextToSize(contactLine.join('  ·  '), contentWidth - 10)[0];
    doc.text(line, margin + 4, yPos + 20);
  }
  yPos += 24 + 8;

  // === ITEMS TABLE ===
  const { hasAnyDiscount, headers, body } = buildItemRows(data.items);
  const columnStyles = hasAnyDiscount
    ? {
        0: { cellWidth: 'auto' as const, halign: 'left' as const },
        1: { cellWidth: 16, halign: 'center' as const },
        2: { cellWidth: 14, halign: 'center' as const },
        3: { cellWidth: 28, halign: 'right' as const },
        4: { cellWidth: 22, halign: 'center' as const },
        5: { cellWidth: 30, halign: 'right' as const },
      }
    : {
        0: { cellWidth: 'auto' as const, halign: 'left' as const },
        1: { cellWidth: 18, halign: 'center' as const },
        2: { cellWidth: 14, halign: 'center' as const },
        3: { cellWidth: 32, halign: 'right' as const },
        4: { cellWidth: 32, halign: 'right' as const },
      };

  autoTable(doc, {
    startY: yPos,
    head: headers,
    body,
    theme: 'grid',
    headStyles: {
      fillColor: corporateDark,
      textColor: white,
      fontStyle: 'bold',
      fontSize: 9,
      cellPadding: 4,
      halign: 'center',
    },
    bodyStyles: { fontSize: 8, cellPadding: 3, textColor: darkText },
    alternateRowStyles: { fillColor: zebra },
    columnStyles,
    styles: { lineColor: corporateMid, lineWidth: 0.2 },
    margin: { left: margin, right: margin },
  });

  const tableEndY = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;
  yPos = tableEndY + 4;

  // === TOTAL (dark bar right) ===
  const totals = pdfQuoteTotals(data);
  const totalBoxWidth = 82;
  const totalBoxHeight = 15;
  const totalBoxX = pageWidth - margin - totalBoxWidth;
  yPos = renderTotalsBreakdown(doc, totals, yPos, {
    margin,
    pageWidth,
    grayText,
    width: totalBoxWidth,
  });
  doc.setFillColor(...corporateDark);
  doc.rect(totalBoxX, yPos, totalBoxWidth, totalBoxHeight, 'F');

  doc.setTextColor(...white);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('TOTAL GERAL', totalBoxX + 4, yPos + 9);
  doc.setFontSize(13);
  doc.text(formatCurrency(totals.total), pageWidth - margin - 4, yPos + 9, { align: 'right' });

  yPos += totalBoxHeight + 8;

  // === OBSERVATIONS ===
  if (data.warranty || data.notes) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...corporateDark);
    doc.text('OBSERVAÇÕES', margin, yPos);
    yPos += 5;
    doc.setDrawColor(...corporateDark);
    doc.setLineWidth(0.3);
    doc.line(margin, yPos, margin + 40, yPos);
    yPos += 4;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...darkText);
    if (data.warranty) {
      const lines = doc.splitTextToSize(`Garantia: ${data.warranty}`, contentWidth);
      doc.text(lines, margin, yPos);
      yPos += 4 * lines.length + 2;
    }
    if (data.notes) {
      const lines = doc.splitTextToSize(data.notes, contentWidth);
      doc.text(lines, margin, yPos);
      yPos += 4 * lines.length + 4;
    }
  }

  // === PIX / TERMS / PHOTOS / SIGNATURE ===
  // Formas aceitas + condições de pagamento. O retorno avança o `yPos`:
  // tudo abaixo (PIX, fotos, assinatura) é posicionado por coordenada.
  // === DIAGNÓSTICO TÉCNICO (Ordem de Serviço) ===
  // Só desenha quando a OS tem os campos preenchidos; devolve o yPos avançado
  // (jsPDF não tem layout — armadilha #13).
  yPos = renderServiceOrderSections(doc, data, yPos, {
    margin,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });

  yPos = renderDiagnosisSection(doc, data, yPos, {
    margin,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });

  // Campos extras do prestador: 0 de avanço quando não há (#L5).
  yPos = renderExtraFields(doc, data, yPos, {
    margin,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });
  yPos = renderPaymentSection(doc, data, yPos, {
    margin,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });

  yPos = await renderPixSection(doc, data, yPos, {
    primaryColor: corporateDark,
    grayText,
    darkText,
    margin,
    pageWidth,
    pageHeight,
  });

  if (data.termsConditions) {
    if (yPos > pageHeight - 60) {
      doc.addPage();
      yPos = margin + 4;
    }
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...corporateDark);
    doc.text('TERMOS E CONDIÇÕES', margin, yPos);
    yPos += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...darkText);
    const lines = doc.splitTextToSize(data.termsConditions, contentWidth);
    doc.text(lines, margin, yPos);
    yPos += 3.5 * lines.length + 4;
  }

  yPos = await renderPhotosSection(doc, data, yPos, {
    darkText,
    grayText,
    grayLight,
    margin,
    pageWidth,
    pageHeight,
  });

  await renderSignatureSection(doc, data, {
    darkText,
    grayText,
    margin,
    pageWidth,
    pageHeight,
    contentBottom: yPos,
  });

  // Dark footer bar
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFillColor(...corporateDark);
    doc.rect(0, pageHeight - 14, pageWidth, 6, 'F');
  }

  renderFooter(doc, data, {
    primaryColor: corporateDark,
    grayText,
    margin,
    pageWidth,
    pageHeight,
  });
}
